import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import { Collection, Pagination } from 'src/types';
import Link from 'next/link';
import { commonCarouselSettings, DEFAULT_PAGINATION } from 'src/lib/constants';
import Slider from 'react-slick';
import ImageWithFallback from 'src/components/ImageWithFallback';
import collectionStyle from 'src/styles/modules/pages/collection.module.scss';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import useQuery from 'src/hooks/useQuery';
import Loading from 'src/components/Loading';

const RelatedCollectionItem: React.FC<{
  klass: string | undefined;
  record: any;
  currentCollection: Collection;
}> = ({ currentCollection, record, klass }) => {
  return (
    <div className={klass}>
      <Link
        legacyBehavior
        prefetch={false}
        href={
          ['BlankCard', 'CountryOrCity'].includes(record.type)
            ? currentCollection.cityId
              ? AppRoutes.cityRoute({ id: record.slug }).toPath()
              : AppRoutes.countryRoute({ id: record.slug }).toPath()
            : AppRoutes.collectionRoute({ id: record.slugCode }).toPath()
        }
      >
        {record.type === 'BlankCard' ? (
          <a className="tw-bg-brand-navy tw-h-full tw-rounded-lg tw-text-brand-green tw-flex tw-justify-center tw-items-center tw-py-9 tw-px-4 tw-cursor-pointer tw-text-center tw-font-styleSans tw-font-semibold tw-underline">
            {record.name}
          </a>
        ) : (
          <a>
            <div className="tw-relative tw-h-full">
              <ImageWithFallback
                record={record.image}
                imgField="webpLgUrl"
                smImgField="webpMdUrl"
                alt={record.image?.caption || record.name}
                className="tw-static tw-object-cover tw-h-full tw-w-full tw-border-2 tw-border-brand-green tw-rounded-lg"
                fill
              />
              <div className="tw-leading-none md:tw-leading-none tw-absolute md:tw-bottom-6 md:tw-left-6 md:tw-right-6 tw-bottom-4 tw-left-5 tw-right-5 md:tw-text-2xl tw-font-styleSans tw-font-semibold tw-text-white tw-z-100">
                {record.location ? record.location.name : record.tag ? record.tag.name : record.name}
              </div>
              <div
                className="tw-w-full tw-h-full tw-absolute tw-top-0 tw-left-0 tw-rounded-lg tw-z-90"
                style={{
                  background: 'linear-gradient(rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.55) 100%)',
                }}
              ></div>
            </div>
          </a>
        )}
      </Link>
    </div>
  );
};

const RelatedCollectionsInner: React.FC<{
  relatedCollections: Collection[];
  nextPage: number | undefined;
  currentCollection: Collection;
  countryOrCityRecord: any;
}> = ({ currentCollection, relatedCollections, nextPage, countryOrCityRecord }) => {
  const translation = useTranslations('components.RelatedCollections');
  const { isScreenMd } = useBreakpoints();
  const blankCard = {
    slug: countryOrCityRecord.slug,
    type: 'BlankCard',
    image: undefined,
    name: translation('allThingsToDo', { name: countryOrCityRecord.name }),
  };
  const items = [...relatedCollections, nextPage ? blankCard : null].filter((o) => o !== null);
  const chunkSize = isScreenMd ? 8 : 6;
  const firstLimitItems = isScreenMd ? 4 : 2;
  const firstGroup = items.slice(0, firstLimitItems);
  const remainingItems = items.slice(firstLimitItems);
  const otherGroups = Array.from({ length: Math.ceil(remainingItems.length / chunkSize) }).map(
    (_, i) => remainingItems.slice(i * chunkSize, i * chunkSize + chunkSize)
  );

  return (
    <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24">
      <div className="container">
        <div className="tw-text-sm tw-uppercase tw-font-semibold tw-text-center tw-mb-2">
          {translation('notDoneExploring')}
        </div>
        <div className="tw-font-styleSans tw-font-semibold md:tw-text-6xl tw-text-5xl tw-text-center md:tw-mb-12 tw-leading-none md:tw-leading-none tw-mb-6">
          {translation('cityCountryCollection', { name: countryOrCityRecord.name })}
        </div>
        <Slider
          {...commonCarouselSettings}
          prevArrow={
            <div className="slick-prev">
              <HiOutlineArrowNarrowLeft size={24} strokeWidth={2} className="tw-text-brand-navy" />
            </div>
          }
          nextArrow={
            <div className="slick-next">
              <HiOutlineArrowNarrowRight size={24} strokeWidth={2} className="tw-text-brand-navy" />
            </div>
          }
          slidesToShow={1}
          slidesToScroll={1}
          arrows={remainingItems.length > 0}
          responsive={[
            {
              breakpoint: 800,
              settings: {
                dots: remainingItems.length > 0,
                slidesToShow: 1,
                arrows: false,
              },
            },
          ]}
          className="md:tw--mx-4 tw--mx-2 md:tw-pb-12 tw-pb-4 slider-v2 carousel-collections"
        >
          {[firstGroup, ...otherGroups].map((group, groupIndex) => (
            <div key={groupIndex} className={collectionStyle.relatedCollectionCard}>
              {groupIndex === 0 ? (
                <div className="md:tw-flex tw-flex-row tw-justify-center tw-h-full">
                  <div className="md:tw-w-1/2 tw-w-full md:tw-h-full tw-h-2/3">
                    <RelatedCollectionItem
                      key={countryOrCityRecord.slug}
                      klass="tw-h-full md:tw-p-4 tw-p-2"
                      record={{ ...countryOrCityRecord, type: 'CountryOrCity' }}
                      currentCollection={currentCollection}
                    />
                  </div>
                  <div
                    className={classNames(
                      'tw-grid tw-grid-cols-2 tw-grid-rows-3 md:tw-grid-rows-2 tw-h-full',
                      {
                        'md:tw-w-1/4 md:tw-grid-cols-1': group.length <= 2,
                        'md:tw-w-1/2 md:tw-grid-cols-2': group.length > 2,
                      }
                    )}
                  >
                    {group.map((collection) => (
                      <RelatedCollectionItem
                        key={collection.slug}
                        klass="md:tw-p-4 tw-p-2"
                        record={collection}
                        currentCollection={currentCollection}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="tw-grid tw-grid-cols-2 tw-grid-rows-3 md:tw-grid-rows-2 md:tw-grid-cols-4 tw-h-full">
                  {group.map((collection) => (
                    <RelatedCollectionItem
                      key={collection.slug}
                      klass="md:tw-p-4 tw-p-2"
                      record={collection}
                      currentCollection={currentCollection}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
};

const RelatedCollections: React.FC<{
  currentCollection: Collection;
  countryOrCityRecord: any;
}> = (props) => {
  const { data, isLoading } = useQuery<{ data: Collection[]; pagination: Pagination }>(
    ApiRoutes.apiRelatedCollectionsRoute({ id: props.currentCollection.id }, {}).toUrl()
  );

  const { data: collections, pagination } = data || { data: [], pagination: DEFAULT_PAGINATION };

  return isLoading ? (
    <Loading className="tw-justify-center tw-my-8" />
  ) : collections ? (
    <RelatedCollectionsInner
      {...props}
      nextPage={pagination.nextPage}
      relatedCollections={collections || []}
    />
  ) : null;
};

export default RelatedCollections;
